/* eslint-disable padding-line-between-statements */
import authService from './auth.service';
import axios from 'axios';
import router from '@/router';
import uuidv4 from 'uuid/v4';

const retryTimeout = 5000;

export const USERS_API_HOST = process.env.VUE_APP_USERS_API_HOST || process.env.VUE_APP_API_HOST;
export const IMPORTER_API_HOST = process.env.VUE_APP_IMPORTER_API_HOST || process.env.VUE_APP_API_HOST;
export const INTERVENERS_API_HOST = process.env.VUE_APP_INTERVENERS_API_HOST || process.env.VUE_APP_API_HOST;
export const ACCOUNTS_API_HOST = process.env.VUE_APP_ACCOUNTS_API_HOST || process.env.VUE_APP_API_HOST;
export const ISSUES_API_HOST = process.env.VUE_APP_ISSUES_API_HOST || process.env.VUE_APP_API_HOST;
export const CALLBACKS_API_HOST = process.env.VUE_APP_CALLBACKS_API_HOST || process.env.VUE_APP_API_HOST;
export const PAYMENTS_API_HOST = process.env.VUE_APP_PAYMENTS_API_HOST || process.env.VUE_APP_API_HOST;
export const ONBOARDING_HOST = process.env.VUE_APP_API_HOST + '/onboarding';

export const USERS_API_CONTEXT = process.env.VUE_APP_USERS_API_CONTEXT || 'users-api';
export const IMPORTER_API_CONTEXT = process.env.VUE_APP_IMPORTER_API_CONTEXT || 'importer-api';
export const INTERVENERS_API_CONTEXT = process.env.VUE_APP_INTERVENERS_API_CONTEXT || 'interveners-api';
export let ACCOUNTS_API_CONTEXT = process.env.VUE_APP_ACCOUNTS_API_CONTEXT || 'accounts-api-private';
export const ISSUES_API_CONTEXT = process.env.VUE_APP_ISSUES_API_CONTEXT || 'issues-api';
export const CALLBACKS_API_CONTEXT = process.env.VUE_APP_CALLBACKS_API_CONTEXT || 'callbacks-api';
export const PAYMENTS_API_CONTEXT = process.env.VUE_APP_PAYMENTS_API_CONTEXT || 'payments-api';

if (process.env.VUE_APP_API_HOST === 'production') {
  ACCOUNTS_API_CONTEXT += '/v1';
}
let xJWTAssertion;
if (process.env.VUE_APP_ENV === 'local') {
  xJWTAssertion = process.env.VUE_APP_LOCAL_X_JTW_ASSERT;
}

const isApplicationJson = /application\/json/i;

const responseHandler = response => {
  try {
    if (isApplicationJson.test(response.request.getResponseHeader('content-type'))) {
      return response.data;
    } else {
      return response;
    }
  } catch (err) {
    return response.data;
  }
};

// TODO: Mejorar manejo de error -> En un 401(no autorizado) ¿no deberíamos hacer un logout?
const errorResponseHandler = async errorResponse => {
  // By default: error setting up request
  console.warn('errorResponse');
  console.warn(errorResponse);
  console.warn(errorResponse.response);
  if (errorResponse.response && errorResponse.response.status === 401) {
    console.warn('Error 401');
    const err401 = new Error();
    err401.name = 'Error 401';
    err401.message = 'No autorizado';
    try {
      // Si viene un 401 intenta refresacar el token
      await authService.tryToRefreshToken();
      // Si sigue por aquí es que se ha refrescado correctamente, o bien, ya era válido
      // En ese caso, se vuelve a crear una instancia de axios para reintentar la petición que falló
      const instance = axios.create();
      instance.interceptors.response.use(
        response => {
          // En caso de acierto en el reintento, se devuelven los datos
          return response.data;
        },
        error => {
          // en caso de error se redirige al login lanzando el error 401
          console.warn(error);
          router.push(`/login`);
          throw err401;
        }
      );
      // Se vuelve a hacer la petición después de un tiempo (retryTimeout)
      setTimeout(() => {
        instance.request(errorResponse.config);
      }, retryTimeout);
    } catch (error) {
      // Captura el error en el refresco del token, si no se refresca es que no está autorizado
      console.warn(error);
      // Se redirige al login y se lanza el error
      router.push(`/login`);
      throw err401;
    }
  }

  if (errorResponse.response && errorResponse.response.status === 404) {
    console.warn('Error 404');
    const err = new Error();
    err.name = 'Error 404';
    err.code = '404';
    err.message = 'No encontrado';
    err.detail =
      errorResponse.response.data && errorResponse.response.data.result && errorResponse.response.data.result.errors
        ? errorResponse.response.data.result.errors[0].message
        : undefined;
    throw err;
  }

  const err = new Error(errorResponse.response);
  err.name =
    errorResponse.response && errorResponse.response.status ? `${errorResponse.response.status}` : `HTTP Error`;
  if (errorResponse.response && errorResponse.response.data && errorResponse.response.data.result) {
    err.message = errorResponse.response.data.result.info;
    if (errorResponse.response.data.result.errors && errorResponse.response.data.result.errors[0]) {
      err.message = `${errorResponse.response.data.result.errors[0].message}`;
    }
  } else {
    err.message = 'No response was received';
  }
  throw err;
};
const apiKeyMap = {
  [process.env.VUE_APP_VALENTUM_URI]: process.env.VUE_APP_VALENTUM_APIKEY || '',
  [process.env.VUE_APP_OMEGA_URI]: process.env.VUE_APP_OMEGA_APIKEY || '',
  [process.env.VUE_APP_TESYS_URI]: process.env.VUE_APP_TESYS_APIKEY || '',
  [process.env.VUE_APP_GESIURIS_URI]: process.env.VUE_APP_GESIURIS_APIKEY || '',
  [process.env.VUE_APP_INNO_URI]: process.env.VUE_APP_INNO_APIKEY || '',
  [process.env.VUE_APP_METAGESTION_URI]: process.env.VUE_APP_METAGESTION_APIKEY || '',
  [process.env.VUE_APP_MAGALLANES_URI]: process.env.VUE_APP_MAGALLANES_APIKEY || ''
};

const getApiKey = url => {
  const hostname = new URL(url).hostname;
  return apiKeyMap[hostname] || 'default-api-key';
};

export { getApiKey };
/**
 * @override
 */
class ApiService {
  /**
   * Crea configuración base de la conexión
   * @return {*} Coniguración de la conexión
   */

  config() {
    const cfg = {
      baseURL: process.env.VUE_APP_USERS_API,
      withCredentials: false,
      headers: {
        Accept: '*/*',
        Authorization: `Bearer ${localStorage.getItem('BOCOAT')}`,
        'X-Request-Id': uuidv4()
      }
    };

    if (process.env.VUE_APP_ENV === 'local') {
      cfg.headers['x-jwt-assertion'] = xJWTAssertion;
    }
    return cfg;
  }

  /**
   * @override
   */
  constructor(baseConfig = undefined) {
    this.connection = axios.create(Object.assign(this.config(), baseConfig));
    this.connection.interceptors.request.use(
      authService.authorizationInterceptor,
      authService.authorizationInterceptorOnError
    );
    this.connection.interceptors.response.use(responseHandler, errorResponseHandler);
  }

  /**
   * @override
   */
  get(url, config = {}) {
    if (!url.includes('$limit=')) {
      return this.connection.get(url, this.parseGetConfigParams(config));
    } else {
      return this.connection.get(url, config);
    }
  }
  /**
   * @override
   */
  getList(url, config = {}) {
    if (!config.params) {
      config.params = {};
    }
    return this.connection.get(url, this.parseGetListConfigParams(config));
  }

  /**
   * @override
   */
  getDownload(url, config = {}, payload) {
    return this.connection.post(url, payload, this.parseGetDownloadConfigParams(config));
  }

  parseGetConfigParams(config) {
    if (!config.params) {
      config.params = {};
    }
    if (!config.params['$limit']) {
      config.params['$limit'] = 25;
    }
    if (config.params['$filter'] === 'null') {
      delete config.params['$filter'];
    }
    return config;
  }
  parseGetListConfigParams(config) {
    const basicConfig = this.parseGetConfigParams(config);
    if (!basicConfig.params['$offset']) {
      basicConfig.params['$offset'] = 0;
    }
    if (basicConfig.params['$filter']) {
      basicConfig.params['$filter'] = btoa(unescape(encodeURIComponent(basicConfig.params['$filter'])));
    }

    return basicConfig;
  }
  parseGetDownloadConfigParams(config) {
    const basicConfig = this.parseGetListConfigParams(config);
    if (!basicConfig.params['$format']) {
      basicConfig.params['$format'] = 'CSV';
    }
    return basicConfig;
  }
  /**
   * @override
   */
  delete(url, config = {}) {
    return this.connection.delete(url, config);
  }

  /**
   * @override
   */
  post(url, payload, config = {}) {
    return this.connection.post(url, payload, config);
  }

  /**
   * @override
   */
  put(url, payload, config = {}) {
    return this.connection.put(url, payload, config);
  }

  /**
   * @override
   */
  patch(url, payload, config = {}) {
    return this.connection.patch(url, payload, config);
  }
}

export default ApiService;
